import type { FormEvent } from 'react';
import React, { useState } from 'react';

import {
  AdornedInput,
  Adornment,
  ButtonGroup,
  Flex,
  FormField,
  FormHelp,
  Input,
  Label,
  Paragraph,
  TextButton,
  ToggleButton,
} from '@zillow/constellation';

import { PETS } from '../../../../constants/index';
import { GENDERS, PET_TYPES } from '../../../../constants/stringConstants';
import type Field from '../../../../models/Field';
import { PetSection } from '../../../../models/index';
import { hyphenateString } from '../../../../utils/stringParsing';

type Props = {
  index: number;
  onFieldChange: (event: FormEvent<HTMLInputElement>) => void;
  onRemovePet: (event: FormEvent<HTMLElement>) => void;
  breedField: Field;
  genderField: Field;
  licenseField: Field;
  nameField: Field;
  typeField: Field;
  weightField: Field;
  breedValue: string;
  genderValue: string;
  licenseValue: string;
  nameValue: string;
  typeValue: string;
  weightValue: string;
  typeHasError: boolean;
  genderHasError: boolean;
  weightHasError: boolean;
};

/*
  Note we maintain a local typeValue state to defer committing the value until a user unfocuses the "Type of pet"
  input field. This allows folks to, for example, type "caterpillar" without the type automatically switching to
  "cat" before the user finishes typing
*/

export default function PetInformation({
  typeValue,
  index,
  onRemovePet,
  onFieldChange,
  breedField,
  genderField,
  licenseField,
  nameField,
  typeField,
  weightField,
  breedValue,
  genderValue,
  licenseValue,
  nameValue,
  weightValue,
  typeHasError,
  genderHasError,
  weightHasError,
}: Props): JSX.Element {
  const [stateTypeValue, setStateTypeValue] = useState(typeValue);
  const [typeIsInitialized, setTypeIsInitialized] = useState(typeValue !== '');

  function onTypeButtonClick(event: FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setTypeIsInitialized(true);
    setStateTypeValue(value);
    onFieldChange(event);
  }

  function onTypeChange(event: FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setTypeIsInitialized(true);
    setStateTypeValue(value);
  }

  // Note we're determining isOtherType based on Props.typeValue rather than the local typeValue.
  // The user might still be typing the local typeValue and we don't want branching logic to be triggered
  // while that value is in flux.
  const isOtherType = typeIsInitialized && PetSection.isOtherType(typeValue);

  return (
    <React.Fragment key={index}>
      {index > 0 && (
        <Flex display="flex" alignItems="center" justifyContent="space-between">
          <Paragraph fontType="bodyHeading">Pet #{index + 1}</Paragraph>
          <TextButton data-index={index} onClick={onRemovePet}>
            Delete
          </TextButton>
        </Flex>
      )}
      <FormField
        marginBottom="sm"
        required
        label={<Label id={hyphenateString(`${PETS.LABEL.TYPE}-${index}`)}>{PETS.LABEL.TYPE}</Label>}
        control={
          <ButtonGroup marginTop="xs" conjoined fluid aria-label="pet type">
            <ToggleButton
              pressed={PetSection.isDogType(typeField.enteredValue)}
              data-field-name={typeField.fieldName}
              onClick={onTypeButtonClick}
              value={PET_TYPES.DOG}
            >
              Dog
            </ToggleButton>
            <ToggleButton
              pressed={PetSection.isCatType(typeField.enteredValue)}
              data-field-name={typeField.fieldName}
              onClick={onTypeButtonClick}
              value={PET_TYPES.CAT}
            >
              Cat
            </ToggleButton>
            <ToggleButton
              pressed={isOtherType}
              data-field-name={typeField.fieldName}
              onClick={onTypeButtonClick}
              value=""
            >
              Other
            </ToggleButton>
          </ButtonGroup>
        }
      />
      {isOtherType ? (
        <FormField
          required
          marginBottom="sm"
          error={typeHasError}
          label={<Label id={hyphenateString(`other-type-${index}`)}>{PETS.LABEL.OTHER_TYPE}</Label>}
          control={
            <Input
              aria-labelledby={hyphenateString(`other-type-${index}`)}
              data-field-name={typeField.fieldName}
              value={stateTypeValue}
              placeholder="(example: parakeet or guinea pig)"
              onChange={onTypeChange}
              onBlur={onFieldChange}
            />
          }
          description={typeHasError && <FormHelp error>{typeField.getErrorMessage()}</FormHelp>}
        />
      ) : (
        <FormField
          optional
          marginBottom="sm"
          label={<Label id={hyphenateString(`${PETS.LABEL.GENDER}-${index}`)}>{PETS.LABEL.GENDER}</Label>}
          control={
            <ButtonGroup marginTop="xs" conjoined fluid aria-label="pet gender">
              <ToggleButton
                pressed={genderValue === GENDERS.MALE}
                data-field-name={genderField.fieldName}
                onClick={onFieldChange}
                value={GENDERS.MALE}
              >
                Male
              </ToggleButton>
              <ToggleButton
                pressed={genderValue === GENDERS.FEMALE}
                data-field-name={genderField.fieldName}
                onClick={onFieldChange}
                value={GENDERS.FEMALE}
              >
                Female
              </ToggleButton>
            </ButtonGroup>
          }
        />
      )}
      <FormField
        optional
        marginBottom="sm"
        label={<Label id={hyphenateString(`${PETS.LABEL.NAME}-${index}`)}>{PETS.LABEL.NAME}</Label>}
        control={
          <Input
            aria-labelledby={hyphenateString(`${PETS.LABEL.NAME}-${index}`)}
            data-field-name={nameField.fieldName}
            data-test="nameField"
            value={nameValue}
            onChange={onFieldChange}
          />
        }
      />
      {!isOtherType && (
        <FormField
          optional
          marginBottom="sm"
          label={<Label id={hyphenateString(`${PETS.LABEL.BREED}-${index}`)}>{PETS.LABEL.BREED}</Label>}
          control={
            <Input
              aria-labelledby={hyphenateString(`${PETS.LABEL.BREED}-${index}`)}
              data-field-name={breedField.fieldName}
              value={breedValue}
              onChange={onFieldChange}
            />
          }
        />
      )}
      <FormField
        optional
        marginBottom="sm"
        error={weightHasError}
        label={<Label id={hyphenateString(`${PETS.LABEL.WEIGHT}-${index}`)}>{PETS.LABEL.WEIGHT}</Label>}
        control={
          <AdornedInput
            inputId={hyphenateString(`AdornedInput-${PETS.LABEL.BREED}-${index}`)}
            rightAdornment={<Adornment>lbs</Adornment>}
            error={weightHasError}
            input={
              <Input
                aria-labelledby={hyphenateString(`${PETS.LABEL.WEIGHT}-${index}`)}
                data-field-name={weightField.fieldName}
                value={weightValue}
                onChange={onFieldChange}
              />
            }
          />
        }
        description={weightHasError && <FormHelp error>{weightField.getErrorMessage()}</FormHelp>}
      />
      <FormField
        optional
        label={<Label id={hyphenateString(`${PETS.LABEL.LICENSE_NUMBER}-${index}`)}>{PETS.LABEL.LICENSE_NUMBER}</Label>}
        control={
          <Input
            aria-labelledby={hyphenateString(`${PETS.LABEL.LICENSE_NUMBER}-${index}`)}
            label={PETS.LABEL.LICENSE_NUMBER}
            data-field-name={licenseField.fieldName}
            value={licenseValue}
            onChange={onFieldChange}
          />
        }
      />
    </React.Fragment>
  );
}
